import { useQuery } from '@apollo/client';
import { type FunctionComponent } from 'react';
import { type EcoRebatesQueryQuery, type EcoRebatesQueryQueryVariables } from '../../../__generated__/graphql-client-types';
import { useCustomer } from '../../../hooks/apollo/customer/customer.hooks';
import { PRODUCT_ECO_REBATES } from '../../../queries/eco-rebates/eco-rebates.queries';
import { type ProductEcoRebate } from '../../../types/product.types';
import { doesWindowExist } from '../../../utils/dom/dom.utils';
import { TextButton } from '../../buttons';
import { RebateButton } from '../../buttons/rebate-button/rebate-button.component';
import { Expandable } from '../../common-components/expandable/expandable.component';
import { GreyBox } from '../../common-components/grey-box/grey-box.component';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { Modal } from '../../common-components/modal/modal.component';

const ProductRebateContent: FunctionComponent<{ ecoRebates: ProductEcoRebate[] }> = ({ ecoRebates }) => {
	return (
		<div>
			{ecoRebates?.map((rebate) => {
				const externalRebateUrl = () => {
					window.open(rebate.submitUrl, '_blank');
				};
				return (
					<div key={rebate.id}>
						<div className={'pa3 tl'}>
							<p className={'b pt0'}>{rebate.title}</p>
							{rebate.description && <p>{rebate.description}</p>}
							{rebate.mailInUrl?.length ? (
								<StyledLink url={rebate.mailInUrl} openInNewTab={true}>
									Qualifying Products & Details
								</StyledLink>
							) : null}
							<Expandable heading="Terms & Conditions" className={`f6 pt3`}>
								<ul className={'theme-black pl4'}>
									{rebate?.details?.map((detail, index) => (
										<li key={index}>{detail}</li>
									))}
								</ul>
							</Expandable>
							<div className="pt3">
								<div className="f6">Rebate available in: {rebate.zipCode}</div>
								{rebate.buyBefore && <div className="f6">Purchase by: {rebate.buyBefore}</div>}
								{rebate.claimBy && <div className="f6">Submit by: {rebate.claimBy}</div>}
								<div className="f6 pt3">
									For questions call{' '}
									<StyledLink url={`tel:${rebate.phoneNumber}`} automationHook={'phone:click'}>
										{rebate.phoneNumber}
									</StyledLink>
								</div>
							</div>
						</div>
						<GreyBox>
							<div className="tl">
								<RebateButton onClick={externalRebateUrl} size={'SMALL'}>
									Submit Online
								</RebateButton>
								{rebate.mailInUrl?.length ? (
									<span className={'pl3 f6'}>
										<StyledLink url={rebate.mailInUrl} openInNewTab={true}>
											Mail in form
										</StyledLink>
									</span>
								) : null}
							</div>
						</GreyBox>
					</div>
				);
			})}
		</div>
	);
};

export type ProductRebateProps = {
	variantIds: number[];
};

/**
 * ProductRebateModal - renders a modal for Rebate
 */
export const ProductRebate: FunctionComponent<ProductRebateProps> = ({ variantIds }) => {
	const { data: locationData } = useCustomer();
	const defaultZipCode: string = locationData?.customer?.location?.zipCode || '';

	const { data: ecoData } = useQuery<EcoRebatesQueryQuery, EcoRebatesQueryQueryVariables>(PRODUCT_ECO_REBATES, {
		variables: { variantIds, zipCode: defaultZipCode },
		skip: !doesWindowExist() || defaultZipCode === ''
	});
	const ecoRebates = ecoData?.currentEcoRebates;
	const rebateIndicatorMessage = ecoRebates?.length && ecoRebates?.length > 1 ? 'Multiple Rebates Available' : 'Rebate Available';
	return !ecoRebates?.length ? null : (
		<div className="f6 pb2 flex pt1">
			<div className="pa1 br2 b mr2" style={{ backgroundColor: '#DFF0D8', color: '#157A46' }}>
				{rebateIndicatorMessage}
			</div>

			<div className="self-center">
				<Modal
					ariaLabel={'Product Rebates'}
					triggerElement={
						<TextButton className="theme-success" data-testid={'ModalTriggerButton'} underline={true}>
							Details
						</TextButton>
					}>
					<ProductRebateContent ecoRebates={ecoRebates} />
				</Modal>
			</div>
		</div>
	);
};
